import { obterUsuario, ultimoEventoLogin } from './usuario';
import { obterHtml, obterJson } from './api';
import { config, formatar, textos as lerTextos, url } from './config';
import { desempilhar, empilhar, irPara, travarFundo } from './sobreposicao';
import { registrarEvento } from './medicao';
import { atualizarContadorSalvas } from './salvas';
import { escolherLocal, guardarBuscaRecente, localDaConexao, pedirLocalizacao, slugDoLocal, urlBusca } from './local';
import { avisarErro, mostrarSessaoExpirada } from './aviso';

const ehCelular = () => !window.matchMedia('(min-width: 960px)').matches;
// Clique com Ctrl/Cmd/Shift ou botão do meio abre o link em outra aba: não interceptar.
const cliqueComum = e => e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
const plural = (n, um, varios) => formatar(n === 1 ? um : varios, n);
let _textos = {};
let _usuario = null;
let _progressoNovo = null;
let _mensagensNovo = null;
let _fotoNova = null;
let _montadoLogado = false;

// ---------- idioma ----------
// A mesma URL no outro idioma, com a busca e os filtros que a página já mudou sem recarregar (como o v1). Sem a cultura
// no começo do caminho (link curto da vaga), vale o href do servidor.
function _urlNoIdioma(codigo) {
    const prefixo = new RegExp('^/' + config().cultura + '(?=/|$)', 'i');
    if (!prefixo.test(location.pathname)) {
        return null;
    }
    const destino = new URL(location.href);
    destino.pathname = destino.pathname.replace(prefixo, '/' + codigo);
    return destino.href;
}

function _origemIdioma(link) {
    if (link.closest('.lsug')) {
        return 'sugestao';
    }
    if (link.closest('#sheetMenu')) {
        return 'gaveta';
    }
    if (link.closest('.fh')) {
        return 'rodape';
    }
    if (link.closest('.cx-lat')) {
        return 'lateral';
    }
    return link.closest('.mi-det') ? 'conta' : 'topo';
}

// ---------- sugestão de idioma ----------
// Pergunta uma vez só, quando a língua do navegador (ou, sem pt/en/es nele, a do país) não é a da página. Não redireciona:
// o HTML é cacheado sem Vary, o Google lê o site de fora do Brasil e o brasileiro no exterior perderia o português.
const _chaveEscolhaIdioma = 'v2-idioma-escolhido';
const _culturas = { pt: 'pt-br', en: 'en-us', es: 'es-cl' };
const _paisesEspanhol = new Set(['AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'ES', 'GQ', 'GT', 'HN', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'SV', 'UY', 'VE']);
const _paisesPortugues = new Set(['AO', 'BR', 'CV', 'GW', 'MZ', 'PT', 'ST', 'TL']);
// Em inglês esses países levam artigo ("in the United States").
const _paisesComArtigo = new Set(['AE', 'BS', 'CF', 'DO', 'GB', 'GM', 'KM', 'KY', 'MH', 'MV', 'NL', 'PH', 'SB', 'TC', 'US']);

function _gravarEscolhaIdioma(codigo) {
    try {
        localStorage.setItem(_chaveEscolhaIdioma, codigo);
    } catch {
        // Sem armazenamento a sugestão nem aparece (ver _iniciarSugestaoIdioma).
    }
}

function _idiomaSugerido(pais) {
    const linguas = navigator.languages?.length ? navigator.languages : [navigator.language || ''];
    const doNavegador = linguas.map(l => _culturas[(l || '').slice(0, 2).toLowerCase()]).find(Boolean);
    if (doNavegador) {
        return { codigo: doNavegador, motivo: 'navegador' };
    }
    if (!pais) {
        return null;
    }
    return { codigo: _paisesEspanhol.has(pais) ? 'es-cl' : _paisesPortugues.has(pais) ? 'pt-br' : 'en-us', motivo: 'pais' };
}

// O país só entra em inglês e espanhol: em português o artigo muda com o país ("no Brasil", "na Argentina").
function _textoMotivo(textos, sugestao, pais) {
    if (pais && pais !== 'BR' && sugestao.codigo !== 'pt-br') {
        try {
            const nome = new Intl.DisplayNames([sugestao.codigo], { type: 'region' }).of(pais);
            if (nome && nome !== pais) {
                return formatar(textos.pais, (sugestao.codigo === 'en-us' && _paisesComArtigo.has(pais) ? 'the ' : '') + nome);
            }
        } catch {
            // Navegador sem Intl.DisplayNames: fica o motivo do navegador (ou nenhum).
        }
    }
    return sugestao.motivo === 'navegador' ? textos.navegador : '';
}

function _nomeDaLingua(codigo, emQualLingua, reserva) {
    try {
        return new Intl.DisplayNames([emQualLingua], { type: 'language' }).of(codigo.slice(0, 2)) || reserva;
    } catch {
        return reserva;
    }
}

function _posicionarSugestao(cartao) {
    const topo = document.getElementById('topo-v2');
    if (!topo || !cartao.isConnected) {
        return;
    }
    cartao.style.setProperty('--lsug-top', Math.round(topo.getBoundingClientRect().bottom + 10) + 'px');
    const globo = topo.querySelector('[data-hdd="idioma"]');
    if (ehCelular() || !globo) {
        return;
    }
    // No computador o cartão sai do globo, com a ponta da seta no meio dele.
    const rg = globo.getBoundingClientRect();
    const largura = document.documentElement.clientWidth;
    const esquerda = Math.max(12, Math.min(rg.right - cartao.offsetWidth, largura - cartao.offsetWidth - 12));
    cartao.style.setProperty('--lsug-left', Math.round(esquerda) + 'px');
    cartao.style.setProperty('--lsug-seta', Math.round(rg.left + rg.width / 2 - esquerda) + 'px');
}

async function _iniciarSugestaoIdioma() {
    const modelo = document.getElementById('tpl-sug-idioma');
    if (!modelo || /bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent)) {
        return;
    }
    try {
        if (localStorage.getItem(_chaveEscolhaIdioma)) {
            return;
        }
    } catch {
        // Sem armazenamento não dá para lembrar a resposta: a pergunta voltaria em toda página.
        return;
    }

    const usuario = await obterUsuario();
    const pais = (usuario?.geoData?.pais || '').toUpperCase();
    const atual = config().cultura;
    const sugestao = _idiomaSugerido(pais);
    if (!sugestao || sugestao.codigo === atual) {
        return;
    }
    const todos = lerTextos(modelo);
    const textos = todos[sugestao.codigo];
    const destino = _urlNoIdioma(sugestao.codigo) || document.querySelector(`a[hreflang="${sugestao.codigo}"]`)?.href;
    if (!textos || !destino) {
        return;
    }

    const cartao = modelo.content.firstElementChild.cloneNode(true);
    cartao.lang = sugestao.codigo.slice(0, 2);
    const motivo = _textoMotivo(textos, sugestao, pais);
    const elMotivo = cartao.querySelector('[data-sug="motivo"]');
    elMotivo.textContent = motivo;
    elMotivo.hidden = !motivo;
    cartao.querySelector('[data-sug="pergunta"]').textContent = textos.pergunta;
    // O "aceitar" é mais um link de idioma: o clique segue o mesmo caminho do topo e do rodapé.
    const aceitar = cartao.querySelector('[data-sug="aceitar"]');
    aceitar.textContent = textos.aceitar;
    aceitar.href = destino;
    aceitar.hreflang = sugestao.codigo;
    const manter = cartao.querySelector('[data-sug="manter"]');
    manter.textContent = formatar(textos.manter, _nomeDaLingua(atual, sugestao.codigo, todos[atual]?.nome || atual));

    const reposicionar = () => _posicionarSugestao(cartao);
    manter.addEventListener('click', () => {
        _gravarEscolhaIdioma(atual);
        registrarEvento('idioma_sugestao_recusada', { idioma_sugerido: sugestao.codigo, idioma_pagina: atual, pais });
        cartao.remove();
        window.removeEventListener('resize', reposicionar);
        document.documentElement.classList.remove('v2-sug-idioma');
    });

    // Logo depois do topo no DOM: o Tab chega nele cedo, sem roubar o foco de quem já está na página.
    document.getElementById('topo-v2')?.after(cartao);
    document.documentElement.classList.add('v2-sug-idioma');
    requestAnimationFrame(reposicionar);
    window.addEventListener('resize', reposicionar);
    registrarEvento('idioma_sugerido', { idioma_sugerido: sugestao.codigo, idioma_pagina: atual, pais, motivo: sugestao.motivo });
}

// ---------- menu suspenso do computador (Ferramentas, Explorar, Idioma e Sua conta) ----------
// Entra no DOM logo depois do botão que o abriu: Tab e Shift+Tab seguem a ordem do topo.
let _hddDe = null;

function _abrirHdd(tipo, ancora, peloTeclado) {
    if (_hddDe === ancora) {
        _fecharHdd();
        return;
    }
    _fecharHdd(true);
    const painel = document.getElementById('hdd');
    const topo = ancora.closest('.site-h');
    const modelo = document.getElementById('tpl-hdd-' + tipo);
    if (!painel || !topo || !modelo) {
        return;
    }
    painel.className = 'hdd hdd--' + tipo;
    // A conta com o nome: "Sua conta" sozinho já é o nome da lateral.
    const nomeConta = formatar(_textos.suaConta, (_usuario?.nome || '').trim().split(' ')[0]);
    painel.setAttribute('aria-label', { conta: nomeConta, idioma: _textos.idiomaRotulo, avisos: _textos.notificacoes, explorar: _textos.explorarRotulo }[tipo] || _textos.ferramentasRotulo);
    painel.replaceChildren(modelo.content.cloneNode(true));
    if (tipo === 'conta' && _usuario) {
        _preencher(painel, _usuario);
    }
    if (tipo === 'avisos') {
        _carregarSino(painel, ancora, peloTeclado);
    }
    ancora.insertAdjacentElement('afterend', painel);
    painel.hidden = false;
    const rt = topo.getBoundingClientRect();
    const ra = ancora.getBoundingClientRect();
    const esquerda = tipo === 'ferramentas' || tipo === 'explorar' ? ra.left - rt.left - 8 : ra.right - rt.left - painel.offsetWidth;
    painel.style.left = Math.max(12, Math.min(esquerda, rt.width - painel.offsetWidth - 12)) + 'px';
    painel.style.top = (ra.bottom - rt.top + 8) + 'px';
    ancora.setAttribute('aria-expanded', 'true');
    _hddDe = ancora;
    // Pelo teclado o foco vai ao 1º item; pelo mouse fica no painel, sem anel de foco.
    requestAnimationFrame(() => (peloTeclado ? painel.querySelector('a,button,summary') : painel)?.focus());
}

// A lista do sino é pessoal: sai de um pedaço sem cache a cada abertura, nunca do HTML da página.
let _sinoPedido = null;

async function _carregarSino(painel, ancora, peloTeclado) {
    _sinoPedido?.abort();
    _sinoPedido = new AbortController();
    const msg = painel.querySelector('[data-sino-msg]');
    try {
        const html = await obterHtml(url('/PainelNotificacoes/SinoV2'), { semCache: true, sinal: _sinoPedido.signal });
        if (_hddDe === ancora && !painel.hidden) {
            painel.innerHTML = html;
            // Pelo teclado o foco espera a lista chegar para ir ao 1º item.
            if (peloTeclado && (document.activeElement === ancora || document.activeElement === painel)) {
                painel.querySelector('a,button')?.focus();
            }
        }
    } catch (e) {
        if (e.name !== 'AbortError' && msg) {
            msg.textContent = msg.dataset.erro;
        }
    }
}

function _fecharHdd(semFoco) {
    const painel = document.getElementById('hdd');
    if (!painel || painel.hidden) {
        return;
    }
    painel.hidden = true;
    _sinoPedido?.abort();
    document.getElementById('hddHome')?.appendChild(painel);
    _hddDe?.setAttribute('aria-expanded', 'false');
    if (!semFoco) {
        _hddDe?.focus();
    }
    _hddDe = null;
}

// ---------- gaveta do celular (Menu do site e, para quem entrou, "Sua conta") ----------
let _menuDe = null;

// A mesma gaveta mostra o menu do site ("Menu" do visitante), a conta ("Perfil" da barra e "Sua conta" do menu) ou, no "Menu"
// de quem entrou ("tudo"), a conta em cima e embaixo só o que o site tem a mais (os itens data-na-conta somem pelo CSS).
function _modoMenu(modo) {
    const conta = document.getElementById('sheetConta');
    const site = document.querySelector('#sheetMenu .mn');
    const comConta = modo !== 'site' && !!conta;
    const soConta = modo === 'conta' && !!conta;
    document.getElementById('sheetMenu')?.classList.toggle('is-tudo', comConta && !soConta);
    if (conta) {
        conta.hidden = !comConta;
    }
    if (site) {
        site.hidden = soConta;
    }
    const titulo = document.getElementById('sheetMenuT');
    const nome = (_usuario?.nome || '').trim().split(' ')[0];
    if (titulo) {
        titulo.textContent = soConta ? _textos.suaContaRotulo : comConta && nome ? formatar(_textos.ola, nome) : _textos.menu;
    }
    document.getElementById('sheetMenuBody')?.setAttribute('aria-label', soConta ? _textos.suaContaRotulo : comConta ? _textos.menu : _textos.menuSite);
}

function _abrirMenu(modo = 'site') {
    const menu = document.getElementById('sheetMenu');
    const fundo = document.getElementById('scrim');
    if (!menu) {
        return;
    }
    if (!menu.hidden) {
        _modoMenu(modo);
        document.getElementById('sheetMenuT')?.focus();
        return;
    }
    _modoMenu(modo);
    _menuDe = document.activeElement;
    if (modo !== 'conta') {
        document.querySelectorAll('[data-menu]').forEach(b => b.setAttribute('aria-expanded', 'true'));
    }
    empilhar('v2Menu', () => _fecharMenu({ peloHistorico: true }));
    fundo.hidden = false;
    requestAnimationFrame(() => fundo.classList.add('is-on'));
    menu.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => menu.classList.add('is-open')));
    travarFundo(true);
    setTimeout(() => document.getElementById('sheetMenuT')?.focus(), 60);
}

function _fecharMenu({ peloHistorico = false, manterFoco = false } = {}) {
    const menu = document.getElementById('sheetMenu');
    const fundo = document.getElementById('scrim');
    if (!menu || menu.hidden) {
        return;
    }
    menu.classList.remove('is-open');
    fundo.classList.remove('is-on');
    setTimeout(() => { if (!menu.classList.contains('is-open')) fundo.hidden = true; }, 220);
    setTimeout(() => { if (!menu.classList.contains('is-open')) menu.hidden = true; }, 320);
    if (!peloHistorico) {
        desempilhar('v2Menu');
    }
    travarFundo(false);
    document.querySelectorAll('[data-menu]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    if (!manterFoco) {
        setTimeout(() => (_menuDe?.isConnected ? _menuDe : document.querySelector('.site-h [data-menu]'))?.focus(), 0);
    }
}

// ---------- barra inferior: some ao rolar para baixo e volta ao subir (só no celular) ----------
function _iniciarBarraInferior() {
    // A base nasce no primeiro scroll: ler o scrollY na carga forçava o layout da página no meio do script (reflow forçado).
    let ultimoY = null;
    let agendado = false;
    const medir = () => {
        const y = window.scrollY;
        if (ultimoY === null) {
            ultimoY = y;
            agendado = false;
            return;
        }
        const raiz = document.documentElement;
        // No fim da página a barra volta: escondida, ela ainda ocupa o espaço dela e sobrava um vão embaixo do rodapé. O fim é chegar
        // a esse espaço, e não ao último pixel: no celular a barra do navegador muda a altura da tela e a conta exata falhava.
        const folga = Math.max(2, parseFloat(getComputedStyle(document.body).paddingBottom) || 0);
        const noFim = y + window.innerHeight >= raiz.scrollHeight - folga;
        if (ehCelular() && y > ultimoY + 4 && y > 140 && !noFim) {
            raiz.classList.add('bars-hide');
        } else if (y < ultimoY - 4 || y < 140 || noFim) {
            raiz.classList.remove('bars-hide');
        }
        ultimoY = y;
        agendado = false;
    };
    const agendar = () => {
        if (!agendado) {
            agendado = true;
            requestAnimationFrame(medir);
        }
    };
    window.addEventListener('scroll', agendar, { passive: true });
    // A barra do navegador (no iPhone ela volta sozinha no fim da página) muda a altura da tela sem um scroll novo.
    window.visualViewport?.addEventListener('resize', agendar);
    window.addEventListener('resize', agendar);
}

// ---------- logado (só no navegador: o HTML é o mesmo para todos) ----------
function _preencher(raiz, u) {
    const nome = (u.nome || '').trim().split(' ')[0];
    const pct = Math.max(0, Math.min(100, u.curriculoProgresso ?? 0));
    const texto = (sel, valor) => raiz.querySelectorAll(`[data-u="${sel}"]`).forEach(el => { el.textContent = valor; });
    const contador = (sel, n) => raiz.querySelectorAll(`[data-u="${sel}"]`).forEach(el => {
        el.hidden = !n;
        el.textContent = n || '';
    });

    texto('nome', nome);
    texto('inicial', (nome || '?').charAt(0).toUpperCase());
    // A foto do currículo no avatar, só depois que ela carrega (se falhar, fica a inicial); sem foto ("sem-foto"), a inicial.
    const foto = u.foto && !/sem-foto/i.test(u.foto) ? u.foto : '';
    // O avatar que já mostra esta foto fica como está: redesenhar (a tela da conta desenha duas vezes) faria a foto piscar.
    const avatares = [...raiz.querySelectorAll('[data-u="inicial"]')].filter(el => el.dataset.foto !== foto);
    const mostrar = comFoto => avatares.forEach(el => {
        el.classList.toggle('avatar--foto', comFoto);
        el.style.backgroundImage = comFoto ? `url("${encodeURI(foto)}")` : '';
        el.dataset.foto = comFoto ? foto : '';
    });
    mostrar(false);
    if (foto) {
        const img = new Image();
        img.onload = () => {
            if (u.foto === foto) {
                mostrar(true);
            }
        };
        img.src = foto;
    }
    texto('ola', formatar(_textos.ola, nome));
    texto('curriculo', formatar(_textos.curriculo, pct));
    raiz.querySelectorAll('[data-u="barra"]').forEach(el => el.setAttribute('aria-label', formatar(_textos.curriculo, pct)));
    raiz.querySelectorAll('[data-u="progresso"]').forEach(el => { el.style.width = pct + '%'; });
    contador('notificacoes', u.notificacoes || 0);
    contador('mensagens', u.mensagens || 0);
    // O sino soma os pedidos e as conversas não lidas; Início e Mensagens, na lateral, ficam cada um com o seu.
    const avisos = (u.notificacoes || 0) + (u.mensagens || 0);
    contador('avisos', avisos);

    const aria = {
        avisos: avisos ? formatar(_textos.notificacoesNovas, plural(avisos, _textos.nova, _textos.novas)) : _textos.notificacoes,
        conta: formatar(_textos.suaConta, nome)
    };
    raiz.querySelectorAll('[data-u-aria]').forEach(el => el.setAttribute('aria-label', aria[el.dataset.uAria]));
}

// ---------- menu lateral do computador (v42/v43) ----------
// A marca fica no aparelho: na próxima página o <head> reserva o lugar da lateral antes da 1ª pintura (_LayoutV2).
const _chaveLogado = 'v2-logado';

function _gravarMarcaLogado(sim) {
    try {
        if (sim) {
            localStorage.setItem(_chaveLogado, '1');
        } else {
            localStorage.removeItem(_chaveLogado);
            sessionStorage.removeItem(_chaveUsuario);
        }
    } catch {
        // Sem armazenamento: a lateral só entra depois do GetUsuarioLogado.
    }
}

// Quem entrou, guardado na aba: a próxima página desenha o topo e a barra assim que o script roda, e o GetUsuarioLogado só
// corrige. Sai junto com a marca (Sair, sessão expirada, servidor dizendo que não está logado), e uma entrada ou saída em outra
// aba o invalida: a aba antiga não mostra a pessoa anterior.
const _chaveUsuario = 'v2-usuario';

function _guardarUsuario(u) {
    try {
        const { id, nome, foto, curriculoProgresso, notificacoes, mensagens, colaborador } = u;
        sessionStorage.setItem(_chaveUsuario, JSON.stringify({ id, nome, foto, curriculoProgresso, notificacoes, mensagens, colaborador, login: ultimoEventoLogin() }));
    } catch {
        // Sem armazenamento: a próxima página espera a resposta.
    }
}

function _usuarioGuardado() {
    // Nas telas de entrar e sair, quem entrou é justamente o que está em jogo: o topo espera a resposta.
    if (document.body.classList.contains('tela-entrar')) {
        return null;
    }
    try {
        const guardado = localStorage.getItem(_chaveLogado) ? JSON.parse(sessionStorage.getItem(_chaveUsuario)) : null;
        return guardado && guardado.login === ultimoEventoLogin() ? guardado : null;
    } catch {
        return null;
    }
}

// Sessão expirada: a próxima página não reserva mais a lateral.
export function esquecerLogado() {
    _gravarMarcaLogado(false);
}

// O Meu currículo muda a porcentagem sem recarregar a página: o menu da conta, montado ao abrir, lê daqui.
export function atualizarProgresso(pct) {
    _progressoNovo = pct;
    if (_usuario) {
        _usuario.curriculoProgresso = pct;
        _guardarUsuario(_usuario);
    }
}

// O Meu currículo troca ou remove a foto sem recarregar: o avatar do topo, da lateral e do Perfil acompanha.
export function atualizarFoto(foto) {
    _fotoNova = foto || '';
    if (!_usuario) {
        return;
    }
    _usuario.foto = _fotoNova;
    _guardarUsuario(_usuario);
    ['.site-h [data-hacts]', '#v2Lat', '#sheetConta', '#sheetMenu .mn-conta-w', '.bnav'].forEach(sel => {
        const raiz = document.querySelector(sel);
        if (raiz) {
            _preencher(raiz, _usuario);
        }
    });
}

// A página de Mensagens lê e recebe conversas sem recarregar: o número do topo, da lateral e o da barra do celular acompanham.
export function atualizarMensagens(n) {
    _mensagensNovo = Math.max(0, n);
    if (!_usuario) {
        return;
    }
    _usuario.mensagens = _mensagensNovo;
    _guardarUsuario(_usuario);
    ['.site-h [data-hacts]', '#v2Lat', '#sheetConta'].forEach(sel => {
        const raiz = document.querySelector(sel);
        if (raiz) {
            _preencher(raiz, _usuario);
            _preencherLateral(raiz, _usuario);
        }
    });
    _preencherMensagensBarra(_usuario);
}

function _medirTopo() {
    const topo = document.getElementById('topo-v2');
    if (topo) {
        document.documentElement.style.setProperty('--v2-topo-h', topo.offsetHeight + 'px');
    }
}

// Só links (nada pessoal): pode entrar antes de o login se confirmar; os números chegam depois. Com o lugar reservado, o script
// logo depois do modelo (_TopoV2) já a criou na leitura do HTML: aqui ela só é completada.
function _inserirLateral() {
    const modelo = document.getElementById('tpl-lateral');
    const topo = document.getElementById('topo-v2');
    if (!modelo || !topo) {
        return;
    }
    let lat = document.getElementById('v2Lat');
    if (!lat) {
        lat = document.createElement('nav');
        lat.className = 'cx-lat';
        lat.id = 'v2Lat';
        lat.append(modelo.content.cloneNode(true));
        topo.after(lat);
    }
    lat.setAttribute('aria-label', _textos.suaContaRotulo);
    // Uma sobreposição aberta antes da resposta já travou o fundo: a lateral nasce travada também.
    lat.inert = document.documentElement.classList.contains('v2-travado');
    document.documentElement.classList.add('v2-lat');
    if (_latRecolhida()) {
        _fecharGruposDaFaixa();
    }
    _medirTopo();
    atualizarContadorSalvas();
}

function _tirarLateral() {
    const lat = document.getElementById('v2Lat');
    if (lat?.contains(document.activeElement)) {
        document.getElementById('conteudo')?.focus();
    }
    lat?.remove();
    document.documentElement.classList.remove('v2-lat');
}

// Recolher e expandir a lateral (☰ ao lado do logo): recolhida, ela é a faixa com ícone e nome curto. A escolha fica no aparelho
// e o <head> da _LayoutV2 a aplica antes da 1ª pintura.
const _latRecolhida = () => document.documentElement.classList.contains('v2-lat-mini');

function _acertarBotaoLateral() {
    const botao = document.querySelector('[data-lat-alternar]');
    if (!botao) {
        return;
    }
    const recolhida = _latRecolhida();
    const rotulo = recolhida ? botao.dataset.rotuloExpandir : botao.dataset.rotuloRecolher;
    botao.setAttribute('aria-expanded', recolhida ? 'false' : 'true');
    botao.setAttribute('aria-label', rotulo);
    botao.title = rotulo;
}

// Na faixa o submenu só aparece flutuando: o grupo aberto no HTML (Ajuda na Central) passa a fechado, senão o leitor de tela
// anunciaria "expandido" sem nada à vista.
function _fecharGruposDaFaixa() {
    document.querySelectorAll('#v2Lat [data-lat-abre]').forEach(botao => {
        const sub = botao.nextElementSibling;
        if (sub?.classList.contains('cx-lat__sub') && !sub.classList.contains('is-voo')) {
            botao.setAttribute('aria-expanded', 'false');
            sub.hidden = true;
        }
    });
}

function _recolherLateral(recolher) {
    document.documentElement.classList.toggle('v2-lat-mini', recolher);
    if (recolher) {
        _fecharGruposDaFaixa();
    }
    try {
        if (recolher) {
            localStorage.setItem('v2-lat-mini', '1');
        } else {
            localStorage.removeItem('v2-lat-mini');
        }
    } catch {
        // Sem armazenamento no navegador: a escolha vale só até trocar de página.
    }
    _acertarBotaoLateral();
    // As telas que medem a largura (carrossel, painel da busca) refazem a conta com o espaço novo.
    window.dispatchEvent(new Event('resize'));
}

function _preencherLateral(raiz, u) {
    const pct = Math.max(0, Math.min(100, u.curriculoProgresso ?? 0));
    raiz.querySelectorAll('[data-u="pct"]').forEach(el => {
        el.textContent = pct + '%';
        el.hidden = false;
    });
    // A Área do colaborador só aparece para quem é colaborador ativo (o servidor responde sem cache).
    raiz.querySelectorAll('[data-u-colaborador]').forEach(el => { el.hidden = !u.colaborador; });
    // O número fica só no visual (aria-hidden): o nome do item diz o que ele conta, como no protótipo.
    const rotulo = (item, extra) => raiz.querySelectorAll(`[data-lat="${item}"]`).forEach(el => {
        if (extra) {
            el.setAttribute('aria-label', `${el.title}, ${extra}`);
        } else {
            el.removeAttribute('aria-label');
        }
    });
    // Os mesmos textos da barra do celular: o "nova/novas" genérico erra o gênero em espanhol ("Mensajes, 2 nuevas").
    rotulo('inicio', u.notificacoes ? plural(u.notificacoes, _textos.pedidoNovo, _textos.pedidosNovos) : '');
    rotulo('curriculo', formatar(_textos.pctCompleto, pct));
    rotulo('mensagens', u.mensagens ? plural(u.mensagens, _textos.mensagemNova, _textos.mensagensNovasQtd) : '');
}

// "Ajuda" e o idioma abrem no lugar, como o collapse da lateral de produção.
function _abrirGrupoLateral(botao) {
    const sub = botao.nextElementSibling;
    if (!sub?.classList.contains('cx-lat__sub')) {
        return;
    }
    const abrir = botao.getAttribute('aria-expanded') !== 'true';
    botao.setAttribute('aria-expanded', String(abrir));
    sub.hidden = !abrir;
}

// Na faixa recolhida, o submenu (Ajuda, idioma) abre flutuando ao lado do item, sem abrir o menu inteiro (pedido do dono, 28/10/2026).
function _fecharVooLateral(focar = false) {
    const sub = document.querySelector('#v2Lat .cx-lat__sub.is-voo');
    if (!sub) {
        return;
    }
    sub.classList.remove('is-voo');
    sub.hidden = true;
    const botao = sub.previousElementSibling;
    botao?.setAttribute('aria-expanded', 'false');
    if (focar) {
        botao?.focus();
    }
}

function _abrirVooLateral(botao) {
    const sub = botao.nextElementSibling;
    if (!sub?.classList.contains('cx-lat__sub')) {
        return;
    }
    const estavaAberto = sub.classList.contains('is-voo');
    _fecharVooLateral();
    if (estavaAberto) {
        return;
    }
    sub.hidden = false;
    sub.classList.add('is-voo');
    botao.setAttribute('aria-expanded', 'true');
    // Na altura do item, abaixo do topo e dentro da tela (o idioma fica perto do fim da faixa).
    const r = botao.getBoundingClientRect();
    const limite = (document.getElementById('v2Lat')?.getBoundingClientRect().top ?? 0) + 8;
    sub.style.top = Math.max(limite, Math.min(r.top, window.innerHeight - sub.offsetHeight - 8)) + 'px';
}

function _localDoTexto(texto) {
    const s = (texto || '').trim();
    return s ? { nome: s, slug: slugDoLocal(s) } : null;
}

async function _buscarPertoDoTopo(t) {
    try {
        const ponto = await pedirLocalizacao();
        escolherLocal({ tipo: 'ponto', origem: 'aparelho', nome: t.pertoDeVoce, ...ponto, cidade: localDaConexao()?.nome || '' });
        irPara(`${url('/vagas')}?perto=${ponto.lat},${ponto.lon}&raio=10`);
    } catch {
        avisarErro(t.localizacaoNegada);
    }
}

// No celular, a pílula "Buscar vagas" do topo (a da vaga e da empresa, e a que o topo põe para quem entrou) abre a busca em tela
// cheia. A home e as telas do bundle da busca cuidam dela (têm a sua); nas outras o modelo #tpl-sov entra na página e o código
// (com o autocomplete) só baixa no primeiro toque.
let _sovTopo = null;

function _tocarPilulaTopo(e) {
    const pilula = e.target.closest('.hnav--busca .mpill');
    if (e.defaultPrevented || !pilula || !document.getElementById('tpl-sov')) {
        return;
    }
    e.preventDefault();
    _sovTopo ??= _carregarSovTopo();
    _sovTopo.then(sov => sov.abrirSov('Q', pilula), () => {
        // Sem os pedaços (rede ruim, aba aberta antes de um deploy), a busca de vagas, que traz a dela.
        _sovTopo = null;
        window.location.assign(url('/vagas'));
    });
}

async function _carregarSovTopo() {
    if (!document.getElementById('sov')) {
        document.body.append(document.getElementById('tpl-sov').content.cloneNode(true));
    }
    const [sov, { iniciarAutocomplete }] = await Promise.all([
        import(/* webpackChunkName: "busca-tela-cheia" */ './buscaTelaCheia'),
        import(/* webpackChunkName: "campo-busca" */ './campoBusca')
    ]);
    const t = sov.textosBusca;
    const q = document.getElementById('sovQ');
    sov.iniciarBuscaTelaCheia({
        valores: () => ({ q: '', l: '' }),
        aoBuscar: (cargo, textoLocal) => {
            const local = _localDoTexto(textoLocal);
            guardarBuscaRecente(cargo, local?.nome || '');
            irPara(urlBusca(cargo, local));
        },
        aoUsarLocalizacao: () => _buscarPertoDoTopo(t)
    });
    iniciarAutocomplete({
        campos: [{ inp: q, tipo: 'q', ctx: 'm' }, { inp: document.getElementById('sovL'), tipo: 'l', ctx: 'm' }],
        textos: t,
        obterLocal: ({ soConexao = false } = {}) => (soConexao ? localDaConexao() : null),
        localDoTexto: _localDoTexto,
        aoEscolherLocal: local => {
            guardarBuscaRecente(q.value, local.nome);
            irPara(urlBusca(q.value, local));
        },
        aoUsarLocalizacao: () => _buscarPertoDoTopo(t)
    });
    return sov;
}

async function _iniciarLogado() {
    // Tela da conta (no-store): quem entrou já vem no HTML (as claims, sem banco); o topo dela entra na hora e os números
    // chegam com a resposta, sem o topo do visitante no meio. Nas públicas, o guardado da página anterior faz o mesmo papel.
    const { conta, pedidos } = config();
    const guardado = _usuarioGuardado();
    // Os números da página anterior até a resposta (sem pular do zero); se a aba guardou outra pessoa, não.
    const anterior = guardado && (!conta || guardado.id === conta.id) ? guardado : {};
    if (conta) {
        _desenharLogado({ ...anterior, ...conta, logado: true, mensagens: anterior.mensagens ?? 0, notificacoes: Number.isInteger(pedidos) ? pedidos : (anterior.notificacoes ?? 0) });
    } else if (guardado) {
        _desenharLogado({ ...guardado, logado: true });
    }
    const u = await obterUsuario();
    // Falha de rede não quer dizer que saiu: a lateral (só links) e a marca ficam. O topo fica como está: o de quem entrou, se
    // veio do guardado; senão o do visitante, com o "Entrar" que estava guardando o lugar do grupo de quem entrou.
    if (u.erro) {
        document.documentElement.classList.add('v2-topo-visitante');
        return;
    }
    // Tela da conta sem a resposta (as claims com zero): ficam os números guardados, sem gravar o zero por cima.
    if (u.semResposta) {
        return;
    }
    // Já desenhado pela tela da conta: a sessão que caiu no meio aparece na próxima ação (401, "Sessão expirada"). Desenhado
    // pelo guardado, a página está mostrando quem saiu: o aviso de sessão expirada recarrega como visitante.
    if (!u.logado) {
        if (!_montadoLogado) {
            _tirarLateral();
            _gravarMarcaLogado(false);
        } else if (!conta) {
            _gravarMarcaLogado(false);
            mostrarSessaoExpirada();
        }
        return;
    }
    _desenharLogado(u);
    _guardarUsuario(u);
    _iniciarSeloMensagens();
}

// Desenha quem entrou: a 1ª vez troca o topo, a lateral, a gaveta e a barra do celular; as seguintes só reescrevem os dados.
function _desenharLogado(u) {
    _usuario = u;
    if (_progressoNovo !== null) {
        u.curriculoProgresso = _progressoNovo;
    }
    if (_mensagensNovo !== null) {
        u.mensagens = _mensagensNovo;
    }
    if (_fotoNova !== null) {
        u.foto = _fotoNova;
    }
    if (!_montadoLogado) {
        _montadoLogado = true;
        _montarLogado();
    }
    ['.site-h [data-hacts]', '#sheetMenu .mn-conta-w', '.bnav'].forEach(sel => {
        const raiz = document.querySelector(sel);
        if (raiz) {
            _preencher(raiz, u);
        }
    });
    ['#v2Lat', '#sheetConta'].forEach(sel => {
        const raiz = document.querySelector(sel);
        if (raiz) {
            _preencher(raiz, u);
            _preencherLateral(raiz, u);
        }
    });
    // Barra do celular (v41): Início é o da conta, com os pedidos das empresas; Mensagens, com as não lidas; Perfil abre a conta.
    const inicio = document.querySelector('.bnav [data-bn="inicio"]');
    if (inicio) {
        const base = inicio.dataset.rotuloBase;
        inicio.setAttribute('aria-label', u.notificacoes ? `${base}, ${plural(u.notificacoes, _textos.pedidoNovo, _textos.pedidosNovos)}` : base);
        let n = inicio.querySelector('.bnav__n');
        if (u.notificacoes && !n) {
            n = document.createElement('span');
            n.className = 'bnav__n';
            n.setAttribute('aria-hidden', 'true');
            inicio.append(n);
        }
        if (n) {
            n.textContent = u.notificacoes || '';
            n.hidden = !u.notificacoes;
        }
    }
    _preencherMensagensBarra(u);
}

function _preencherMensagensBarra(u) {
    const msg = document.querySelector('.bnav [data-bn="mensagens"]');
    if (!msg) {
        return;
    }
    const base = msg.dataset.rotuloBase;
    msg.setAttribute('aria-label', u.mensagens ? `${base}, ${plural(u.mensagens, _textos.mensagemNova, _textos.mensagensNovasQtd)}` : base);
    let n = msg.querySelector('.bnav__n');
    if (u.mensagens && !n) {
        n = document.createElement('span');
        n.className = 'bnav__n';
        n.setAttribute('aria-hidden', 'true');
        msg.append(n);
    }
    if (n) {
        n.textContent = u.mensagens || '';
        n.hidden = !u.mensagens;
    }
}

function _montarLogado() {
    _gravarMarcaLogado(true);
    // Menu suspenso aberto pode morar dentro do [data-hacts], que vai ser trocado: sem fechar antes, o #hdd sai do DOM.
    _fecharHdd(true);

    const acoes = document.querySelector('.site-h [data-hacts]');
    const modeloAcoes = document.getElementById('tpl-acts-logado');
    if (acoes && modeloAcoes) {
        acoes.replaceChildren(modeloAcoes.content.cloneNode(true));
    }

    // A pílula da busca no celular em todas as telas, ao lado do menu (que só aparece no computador e é o mesmo do visitante).
    const nav = document.querySelector('.site-h .hnav');
    const modeloBusca = document.getElementById('tpl-busca-logado');
    if (nav && modeloBusca && !document.querySelector('.site-h .mpill')) {
        nav.after(modeloBusca.content.cloneNode(true));
    }

    document.querySelectorAll('[data-href-logado]').forEach(a => { a.href = a.dataset.hrefLogado; });

    _inserirLateral();

    const mn = document.querySelector('#sheetMenu .mn');
    if (mn) {
        mn.querySelectorAll('[data-visitante]').forEach(el => el.remove());
        const topo = document.getElementById('tpl-menu-logado-topo');
        const fim = document.getElementById('tpl-menu-logado-fim');
        if (topo) {
            mn.prepend(topo.content.cloneNode(true));
        }
        if (fim) {
            mn.append(fim.content.cloneNode(true));
        }
    }

    // "Perfil" do celular (v45): a lista da lateral, sem o idioma (no celular de hoje ele fica fora da lateral). Vem antes do
    // menu do site, que no "Menu" de quem entrou aparece embaixo dela.
    const corpo = document.getElementById('sheetMenuBody');
    const modeloLat = document.getElementById('tpl-lateral');
    if (corpo && modeloLat && !document.getElementById('sheetConta')) {
        const conta = document.createElement('div');
        conta.className = 'cx-lat cx-lat--folha';
        conta.id = 'sheetConta';
        conta.hidden = true;
        conta.append(modeloLat.content.cloneNode(true));
        conta.querySelectorAll('[data-lat-computador]').forEach(el => el.remove());
        corpo.prepend(conta);
        atualizarContadorSalvas();
    }

    const inicio = document.querySelector('.bnav [data-bn="inicio"]');
    if (inicio) {
        if (inicio.hasAttribute('data-bn-publico')) {
            inicio.removeAttribute('aria-current');
        }
        // Para quem entrou ele leva ao Meu painel: "Painel", como na lateral.
        if (inicio.dataset.rotuloLogado) {
            inicio.querySelector('.bnav__l').textContent = inicio.dataset.rotuloLogado;
            inicio.setAttribute('aria-label', inicio.dataset.rotuloLogado);
        }
        inicio.dataset.rotuloBase = inicio.getAttribute('aria-label') || '';
    }
    const salvas = document.querySelector('.bnav [data-bn="salvas"]');
    const modeloMsg = document.getElementById('tpl-bn-mensagens');
    if (salvas && modeloMsg) {
        const msg = modeloMsg.content.firstElementChild.cloneNode(true);
        msg.dataset.rotuloBase = msg.getAttribute('aria-label') || '';
        salvas.replaceWith(msg);
    }
    const perfil = document.querySelector('.bnav [data-bn="perfil"]');
    if (perfil) {
        perfil.querySelector('.bnav__l').textContent = perfil.dataset.rotuloLogado;
        perfil.setAttribute('aria-label', perfil.dataset.rotuloLogado);
        perfil.dataset.abreConta = '1';
        perfil.setAttribute('aria-haspopup', 'dialog');
        // A foto (ou a inicial) no lugar do ícone, para o Perfil não parecer o "Entrar" do visitante; o _preencher a põe.
        const caixa = document.createElement('span');
        caixa.className = 'ico bnav__av';
        caixa.setAttribute('aria-hidden', 'true');
        const avatar = document.createElement('span');
        avatar.className = 'avatar';
        avatar.dataset.u = 'inicial';
        caixa.append(avatar);
        perfil.querySelector('svg.ico')?.replaceWith(caixa);
    }

    document.documentElement.classList.add('v2-logado');
    _medirTopo();
}

// ---------- número de Mensagens fora da tela de Mensagens ----------
// Com conversa não lida, o número segue o servidor a cada 60 s, só com a aba visível (a tela de Mensagens tem o pulso dela;
// a v2 não tem mais tempo real). Sem não lida, nada: a conversa nova aparece na próxima página.
const _SELO_MS = 60000;
// Servidor com erro (5xx), travado (tempo esgotado) ou a cota estourada (429): o intervalo dobra até 10 min e volta ao normal no
// primeiro sucesso.
const _SELO_MAX_MS = 10 * 60000;
let _seloAtraso = _SELO_MS;
let _seloTimer = 0;
let _seloEm = 0;

function _agendarSelo() {
    clearTimeout(_seloTimer);
    if (_usuario?.mensagens > 0) {
        _seloTimer = setTimeout(_pulsarSelo, _seloAtraso);
    }
}

async function _pulsarSelo() {
    clearTimeout(_seloTimer);
    // Aba oculta: o visibilitychange chama de novo quando ela voltar.
    if (document.visibilityState !== 'visible' || !_usuario?.mensagens) {
        return;
    }
    _seloEm = Date.now();
    try {
        const r = await obterJson(url('/PainelMensagensAntigo/Selo'), { semCache: true, silencioso: true });
        _seloAtraso = _SELO_MS;
        if (Number.isInteger(r?.n)) {
            atualizarMensagens(r.n);
        }
    } catch (e) {
        // Sessão acabou (401) ou tela fora do ar no host (404): para sem o aviso de sessão.
        if ([401, 403, 404].includes(e?.status)) {
            return;
        }
        if (e?.status === 429 || e?.status >= 500 || e?.name === 'TimeoutError') {
            _seloAtraso = Math.min(_seloAtraso * 2, _SELO_MAX_MS);
        }
    }
    _agendarSelo();
}

function _iniciarSeloMensagens() {
    // No hotsite genérico o Selo conta só as conversas dele; no tema exclusivo o número vem zerado e o selo nem pulsa.
    if (document.getElementById('cxMsg') || _seloEm) {
        return;
    }
    _seloEm = Date.now();
    // Volta à aba: pulsa na hora se já passou 1 minuto; senão, no que falta.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || !_usuario?.mensagens) {
            return;
        }
        clearTimeout(_seloTimer);
        _seloTimer = setTimeout(_pulsarSelo, Math.max(0, _seloAtraso - (Date.now() - _seloEm)));
    });
    _agendarSelo();
}

export function iniciarTopo() {
    _textos = lerTextos(document.getElementById('topo-v2'));

    document.addEventListener('click', e => {
        const t = e.target;
        // O submenu flutuante da faixa fecha no toque fora dele (o do próprio item é tratado abaixo).
        const voo = document.querySelector('#v2Lat .cx-lat__sub.is-voo');
        if (voo && !voo.contains(t) && !t.closest('[data-lat-abre]')) {
            _fecharVooLateral();
        }
        const botaoHdd = t.closest('[data-hdd]');
        if (botaoHdd) {
            e.preventDefault();
            _abrirHdd(botaoHdd.dataset.hdd, botaoHdd, e.detail === 0);
            return;
        }
        const painel = document.getElementById('hdd');
        if (painel && !painel.hidden && !t.closest('#hdd')) {
            _fecharHdd(true);
        }
        if (t.closest('#hdd a, #hdd button')) {
            _fecharHdd(true);
        }
        // Topo, gaveta, menu da conta, rodapé e sugestão: só os links de idioma têm hreflang.
        const idioma = t.closest('a[hreflang]');
        if (idioma && cliqueComum(e)) {
            // Qualquer escolha de idioma conta como resposta: a sugestão não aparece mais.
            _gravarEscolhaIdioma(idioma.hreflang);
            // O idioma em uso não recarrega: o href do servidor levaria à busca sem os filtros.
            if (idioma.hasAttribute('aria-current')) {
                e.preventDefault();
                idioma.closest('details')?.removeAttribute('open');
                _fecharVooLateral();
                return;
            }
            registrarEvento('idioma_trocado', { de: config().cultura, para: idioma.hreflang, origem: _origemIdioma(idioma) });
            const destino = _urlNoIdioma(idioma.hreflang);
            if (destino) {
                e.preventDefault();
                irPara(destino);
                return;
            }
        }
        const botaoMenu = t.closest('[data-menu]');
        if (botaoMenu) {
            e.preventDefault();
            _abrirMenu(botaoMenu.dataset.menu || 'site');
            return;
        }
        if (t.closest('[data-abre-conta]')) {
            e.preventDefault();
            _abrirMenu('conta');
            return;
        }
        const grupo = t.closest('[data-lat-abre]');
        if (grupo) {
            if (_latRecolhida() && grupo.closest('#v2Lat')) {
                _abrirVooLateral(grupo);
                return;
            }
            _abrirGrupoLateral(grupo);
            return;
        }
        // Na próxima página o <head> não reserva mais a lateral.
        if (t.closest('[data-sair]')) {
            _gravarMarcaLogado(false);
        }
        const link = t.closest('#sheetMenu a[href]');
        if (link) {
            // A página nova entra no lugar da entrada da gaveta: o history.back() do fechar concorreria com o link.
            if (cliqueComum(e) && !link.target) {
                e.preventDefault();
                irPara(link.href);
                return;
            }
            _fecharMenu({ manterFoco: true });
        }
        if (t.closest('#sheetMenu [data-close]') || t.closest('#scrim')) {
            _fecharMenu();
            return;
        }
        const pular = t.closest('[data-skip]');
        if (pular) {
            e.preventDefault();
            const alvo = document.getElementById(pular.dataset.skip);
            if (alvo) {
                if (!alvo.hasAttribute('tabindex')) {
                    alvo.setAttribute('tabindex', '-1');
                }
                alvo.focus();
            }
        }
    });

    document.addEventListener('keydown', e => {
        if (e.key !== 'Escape' || document.querySelector('dialog[open]')) {
            return;
        }
        const voo = document.querySelector('#v2Lat .cx-lat__sub.is-voo');
        if (voo) {
            // O foco só volta ao item se estava no painel ou nele (um Esc num campo da página não o puxa para a faixa).
            _fecharVooLateral(voo.contains(document.activeElement) || document.activeElement === voo.previousElementSibling);
            return;
        }
        const painel = document.getElementById('hdd');
        if (painel && !painel.hidden) {
            _fecharHdd();
            return;
        }
        if (!document.getElementById('sheetMenu')?.hidden) {
            _fecharMenu();
        }
    });

    document.getElementById('hdd')?.addEventListener('focusout', () => setTimeout(() => {
        const p = document.getElementById('hdd');
        if (p && !p.hidden && !p.contains(document.activeElement) && document.activeElement !== _hddDe) {
            _fecharHdd(true);
        }
    }, 0));

    window.addEventListener('resize', () => {
        _fecharHdd(true);
        _fecharVooLateral();
    });
    // O submenu flutuante fica preso à altura do item: rolar a faixa o fecha.
    document.addEventListener('scroll', e => {
        if (e.target?.id === 'v2Lat') {
            _fecharVooLateral();
        }
    }, { capture: true, passive: true });
    // O foco que sai do painel e do item dele (Tab até o conteúdo) fecha o painel: aberto, ele cobriria o que ganhou o foco.
    document.addEventListener('focusout', () => setTimeout(() => {
        const voo = document.querySelector('#v2Lat .cx-lat__sub.is-voo');
        const ativo = document.activeElement;
        if (voo && ativo && ativo !== document.body && !voo.contains(ativo) && ativo !== voo.previousElementSibling) {
            _fecharVooLateral();
        }
    }, 0));
    // Na janela, depois dos listeners das páginas: as que trazem a busca em tela cheia tratam a pílula e chamam preventDefault.
    window.addEventListener('click', _tocarPilulaTopo);
    // A lateral começa embaixo do topo, que muda de altura com a fonte do aparelho.
    const topo = document.getElementById('topo-v2');
    if (topo && 'ResizeObserver' in window) {
        new ResizeObserver(_medirTopo).observe(topo);
    }
    if (document.documentElement.classList.contains('v2-lat')) {
        _inserirLateral();
    }
    _acertarBotaoLateral();
    document.querySelector('[data-lat-alternar]')?.addEventListener('click', () => _recolherLateral(!_latRecolhida()));
    _iniciarBarraInferior();
    _iniciarLogado();
    _iniciarSugestaoIdioma();
}
